<template>
	<v-card dark outlined class="fill-height" style="overflow:hidden">

		<!-- DRAG HANDLE — required for vue-grid-layout -->
		<v-system-bar lights-out dark class="vue-draggable-handle" style="cursor:move">
			<v-icon small color="amber">mdi-chart-bar</v-icon>
		<span class="white--text subtitle-2 text-uppercase ml-1 text-truncate">
			{{ $ml.get('alarm_stats') }} — {{ alarmName }}
		</span>
			<v-spacer/>
			<v-icon x-small :color="wsConnected ? 'green' : 'red'">mdi-circle</v-icon>
			<v-btn icon x-small @click="shiftConfigOpen = true">
				<v-icon x-small color="white">mdi-cog</v-icon>
			</v-btn>
			<v-btn icon x-small class="error ml-1" @click="$emit('close_component', layout_id)">
				<v-icon x-small color="white">mdi-close</v-icon>
			</v-btn>
		</v-system-bar>

		<!-- CONTENT — must have no-drag class -->
		<div class="no-drag" style="height:calc(100% - 24px);overflow-y:auto;padding:8px">

			<!-- Loading overlay -->
			<v-overlay v-if="loading" absolute z-index="10">
				<div class="text-center">
					<v-progress-circular indeterminate size="64" color="orange lighten-1"></v-progress-circular>
					<div class="mt-3 caption grey--text">{{ $ml.get('please_wait') }}</div>
				</div>
			</v-overlay>

			<!-- Error state -->
			<v-alert
				v-else-if="error"
				type="error"
				prominent
				class="ma-2"
			>
				<v-row align="center" no-gutters>
					<v-col class="grow">{{ error }}</v-col>
					<v-col class="shrink">
					<v-btn small outlined color="white" class="ml-4" @click="_fetchAll">
						<v-icon small left>mdi-refresh</v-icon>
						{{ $ml.get('alarm_stats_retry') }}
					</v-btn>
					</v-col>
				</v-row>
			</v-alert>

			<!-- Dashboard content -->
			<template v-else>

			<!-- KPI Cards row -->
			<stats-kpi-cards
				:total-count="statsSnapshot.total_counted || 0"
				:good-count="statsSnapshot.good_count || 0"
				:defect-count="statsSnapshot.defect_count || 0"
				:stoppages-count="statsSnapshot.stoppage_count || 0"
				:total-idle-ms="totalIdleMs"
				:per-minute="statsSnapshot.rate_per_min || 0"
			></stats-kpi-cards>

				<!-- Middle row: Rate gauge + Shift Projection + OEE -->
				<v-row class="mb-2">
				<!-- Rate gauge -->
				<v-col cols="12" md="4">
					<stats-current-rate
						:current-rate="statsSnapshot.rate_per_min || 0"
						:target-rate="shiftConfig.target_rate || 0"
						:peak-rate="statsSnapshot.peak_rate || 0"
						:avg-rate="statsSnapshot.avg_rate || 0"
					></stats-current-rate>
				</v-col>

				<!-- Shift projection -->
				<v-col cols="12" md="4">
					<stats-shift-projection
						:upm="statsSnapshot.rate_per_min || 0"
						:total-counted="statsSnapshot.total_counted || 0"
						:shift-elapsed-ms="shiftElapsedMs"
						:shift-duration-ms="shiftDurationMs"
						:shift-config="shiftConfig"
					></stats-shift-projection>
				</v-col>

				<!-- OEE -->
				<v-col cols="12" md="4">
					<stats-oee
						:availability="statsSnapshot.availability || 0"
						:performance="statsSnapshot.performance || 0"
						:quality="statsSnapshot.quality || 0"
						:overall="statsSnapshot.oee || 0"
					></stats-oee>
				</v-col>
				</v-row>

				<!-- Throughput chart -->
				<v-row class="mb-2">
					<v-col cols="12">
						<stats-throughput-chart
							:throughput-data="throughputSeries"
						></stats-throughput-chart>
					</v-col>
				</v-row>

				<!-- Stoppage log -->
				<v-row>
					<v-col cols="12">
						<stats-stoppage-log
							:stoppages="stoppages"
							:total-idle-ms="totalIdleMs"
							@update:note="handleStoppageNoteUpdate"
						></stats-stoppage-log>
					</v-col>
				</v-row>

			</template>

			<!-- Shift config dialog -->
			<stats-shift-config
				v-model="shiftConfigOpen"
				:config="shiftConfig"
				@save="handleShiftConfigSave"
			></stats-shift-config>

			<!-- Snackbar -->
			<v-snackbar
				v-model="snackbar.show"
				:color="snackbar.color"
				:timeout="snackbar.timeout"
				top
				right
			>
				{{ snackbar.message }}
				<v-btn
					small
					text
					color="white"
					@click="snackbar.show = false"
				>
					<v-icon small>mdi-close</v-icon>
				</v-btn>
			</v-snackbar>

		</div>
	</v-card>
</template>

<script>
import io                     from 'socket.io-client';
import StatsKpiCards          from './StatsKpiCards.vue';
import StatsCurrentRate       from './StatsCurrentRate.vue';
import StatsShiftProjection   from './StatsShiftProjection.vue';
import StatsOee               from './StatsOee.vue';
import StatsThroughputChart   from './StatsThroughputChart.vue';
import StatsStoppageLog       from './StatsStoppageLog.vue';
import StatsShiftConfig       from './StatsShiftConfig.vue';

export default {
	name : 'alarm_stats_widget',

	components : {
		StatsKpiCards,
		StatsCurrentRate,
		StatsShiftProjection,
		StatsOee,
		StatsThroughputChart,
		StatsStoppageLog,
		StatsShiftConfig,
	},

	props : ['layout', 'layout_id', 'info_to_component', 'ref_to_component'],

	data() {
		return {
			// ── Core state ──────────────────────────────────────────────
			loading          : false,
			error            : null,
			socket           : null,
			wsConnected      : false,

			// Shift config dialog visibility
			shiftConfigOpen  : false,

			// Snackbar state
			snackbar : {
				show    : false,
				message : '',
				color   : 'success',
				timeout : 4000,
			},

			// Stats defaults — guards against undefined WS payloads
			_statsDefaults : {
				total_counted         : 0,
				good_count            : 0,
				defect_count          : 0,
				quality_pct           : 0,
				defect_rate           : 0,
				rate_per_min          : 0,
				rate_per_hour         : 0,
				peak_rate             : 0,
				avg_rate              : 0,
				stoppage_count        : 0,
				total_idle_time       : 0,
				is_stopped            : false,
				current_stoppage_duration: 0,
				oee                   : 0,
				availability          : 0,
				performance           : 0,
				quality               : 0,
				projected_total       : 0,
				remaining_minutes     : 0,
				shift_progress_pct    : 0,
			},

			// ── Stats snapshot (populated by REST + WS) ──
			statsSnapshot : {
				alarm_id              : null,
				total_counted         : 0,
				good_count            : 0,
				defect_count          : 0,
				quality_pct           : 0,
				defect_rate           : 0,
				rate_per_min          : 0,
				rate_per_hour         : 0,
				peak_rate             : 0,
				avg_rate              : 0,
				stoppage_count        : 0,
				total_idle_time       : 0,
				is_stopped            : false,
				current_stoppage_duration: 0,
				oee                   : 0,
				availability          : 0,
				performance           : 0,
				quality               : 0,
				projected_total       : 0,
				remaining_minutes     : 0,
				shift_progress_pct    : 0,
			},

			// ── Throughput series ────────────────────────────────
			throughputSeries : [],

			// ── Stoppage log ─────────────────────────────────────
			stoppages : [],

			// ── Shift config ────────────────────────────────────
			shiftConfig : {
				shift_start          : '08:00',
				shift_duration_min   : 540,
				target_rate          : 0,
				stoppage_threshold_ms: 5000,
			},
		};
	}, // data

	computed : {
		// Alarm ID from info_to_component (widget mode)
		alarmId() {
			return this.info_to_component ? Number(this.info_to_component.alarm_id) : null;
		},

		// Alarm name from info_to_component
		alarmName() {
			return this.info_to_component ? this.info_to_component.alarm_name : '';
		},

		// Total idle time in ms — from backend stats snapshot (authoritative)
		totalIdleMs() {
			return this.statsSnapshot.total_idle_time || 0;
		},

		// Shift duration in ms — derived from shiftConfig (local config, not API)
		shiftDurationMs() {
			return (this.shiftConfig.shift_duration_min || 540) * 60000;
		},

		// Shift elapsed in ms — computed from shift_progress_pct × shiftDurationMs
		shiftElapsedMs() {
			const pct = this.statsSnapshot.shift_progress_pct || 0;
			return Math.round(pct * this.shiftDurationMs);
		},

		// Shift progress as a percentage (0-100)
		shiftProgress() {
			return Math.min(100, Math.round((this.statsSnapshot.shift_progress_pct || 0) * 100));
		},

		// Projected total for end of shift
		projectedTotal() {
			const upm       = this.statsSnapshot.rate_per_min   || 0;
			const counted   = this.statsSnapshot.total_counted   || 0;
			const remaining = this.statsSnapshot.remaining_minutes || 0;
			if (!upm) return counted;
			return Math.round((upm * remaining) + counted);
		},

		// OEE color badge — oee is a 0-1 fraction from the backend
		oeeColor() {
			const overall = this.statsSnapshot.oee || 0;
			if (overall >= 0.85) return 'success';
			if (overall >= 0.60) return 'warning';
			return 'error';
		},
	}, // computed

	watch : {
		// React to alarm_id change — re-fetch + reconnect socket
		'info_to_component.alarm_id'(newId, oldId) {
			if (newId && newId !== oldId) {
				this._disconnectSocket();
				this._loadShiftConfigFromStorage();
				this._fetchAll();
			}
		},
	}, // watch

	mounted() {
		// Guard: widget may render before info_to_component is ready
		if (!this.alarmId) return;
		this._loadShiftConfigFromStorage();
		this._fetchAll();
	},

	beforeDestroy() {
		this._disconnectSocket();
	},

	methods : {

		// ─────────────────────────────────────────────────────────────────
		// REST data fetching
		// ─────────────────────────────────────────────────────────────────

		async _fetchAll() {
			if (!this.alarmId) return;

			this.loading = true;
			this.error   = null;

			try {
				const baseUrl = this.$URL + '/api/v1/alarm/' + this.alarmId;

				// Build today's start (local midnight) as ISO for stoppages filter
				const now = new Date();
				const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();

				const [statsRes, throughputRes, stoppagesRes] = await Promise.all([
					this.$axios.get(baseUrl + '/stats'),
					// timeframe=day acota a las últimas 24h (~1440 buckets). Sin él, el endpoint
					// devuelve TODA la historia por minuto (decenas de miles de filas) y bloquea el front.
					this.$axios.get(baseUrl + '/stats/throughput?granularity=minute&timeframe=day'),
					this.$axios.get(baseUrl + '/stats/stoppages?from=' + todayStart + '&limit=200'),
				]);

				// Merge with defaults first to guard undefined fields
				const rawStats = (statsRes.data && typeof statsRes.data === 'object') ? statsRes.data : {};
				this.statsSnapshot    = { ...this.statsSnapshot, ...rawStats };
				this.throughputSeries = Array.isArray(throughputRes.data) ? throughputRes.data : [];
				const stoppagesPayload = stoppagesRes.data;
				this.stoppages        = stoppagesPayload && Array.isArray(stoppagesPayload.stoppages)
					? stoppagesPayload.stoppages
					: (Array.isArray(stoppagesPayload) ? stoppagesPayload : []);

			} catch (err) {
			const status = err && err.response && err.response.status;
			if (status === 404) {
				this.error = this.$ml.get('alarm_stats_not_found');
			} else {
				const msg = err && err.message ? err.message : this.$ml.get('alarm_stats_load_error');
				this.error = msg;
			}
				console.error('[AlarmStatsWidget] _fetchAll error:', err);
			} finally {
				this.loading = false;
			}

			// Connect socket only if REST fetch succeeded (404 → no WS)
			if (!this.error) {
				this._connectSocket();
			}
		},

		// ─────────────────────────────────────────────────────────────────
		// WebSocket real-time updates
		// ─────────────────────────────────────────────────────────────────

		_connectSocket() {
			// Tear down existing socket before creating a new one
			this._disconnectSocket();

			const socketUrl = this.$protocol_socket + this.$localsocket + ':' + this.$port_socket + '/workspace-data';

			this.socket = io(socketUrl, {
				transports         : ['websocket'],
				reconnection       : true,
				reconnectionDelay  : 2000,
				reconnectionAttempts: 10,
			});

			this.socket.on('connect', () => {
				this.wsConnected = true;
			});

			// Show transient snackbar on WS disconnect
			this.socket.on('disconnect', () => {
				this.wsConnected = false;
				this._showSnackbar(this.$ml.get('alarm_stats_ws_lost'), 'warning', 6000);
			});

			this.socket.on('reconnect', () => {
				this.wsConnected = true;
				this._showSnackbar(this.$ml.get('alarm_stats_ws_reconnected'), 'success', 3000);
			});

			// alarm_stats — snapshot refresh
			// Wrapped in try/catch — skip malformed payloads silently
			this.socket.on('alarm_stats', (payload) => {
				try {
					if (!payload || typeof payload !== 'object') return;
					if (Number(payload.alarm_id) !== this.alarmId) return;
					// Merge with defaults to ensure all fields exist.
					// Backend sends flat fields — no nested oee object.
					this.statsSnapshot = {
						...this._statsDefaults,
						...this.statsSnapshot,
						...payload,
					};
				} catch (e) {
					console.warn('[AlarmStatsWidget] alarm_stats payload error:', e);
				}
			});

			// alarm_throughput — live throughput chart refresh (every 5s)
			// WS sends the last ~60 min from RingBuffer. We MERGE with historical
			// REST data so we don't lose older buckets loaded on mount.
			this.socket.on('alarm_throughput', (payload) => {
				try {
					if (!payload || typeof payload !== 'object') return;
					if (Number(payload.alarm_id) !== this.alarmId) return;
					if (!Array.isArray(payload.buckets) || payload.buckets.length === 0) return;

					const wsBuckets = payload.buckets;
					const existing  = this.throughputSeries || [];

					// Find the earliest WS bucket time
					const wsFirstTime = wsBuckets[0].bucket_time;

					// Keep all historical buckets BEFORE the WS range, then append WS data
					const historical = existing.filter(b => b.bucket_time < wsFirstTime);
					this.throughputSeries = [...historical, ...wsBuckets];
				} catch (e) {
					console.warn('[AlarmStatsWidget] alarm_throughput payload error:', e);
				}
			});

			// alarm_stoppage — live stoppage push/close
			// Wrapped in try/catch — skip malformed payloads silently
			this.socket.on('alarm_stoppage', (payload) => {
				try {
					if (!payload || typeof payload !== 'object') return;
					if (Number(payload.alarm_id) !== this.alarmId) return;

					if (payload.type === 'stoppage_start') {
						if (!payload.stoppage || typeof payload.stoppage !== 'object') return;
						const newStoppage = {
							...payload.stoppage,
							device_id : payload.device_id,
						};
						this.stoppages = [newStoppage, ...this.stoppages];

					} else if (payload.type === 'stoppage_end') {
						if (!payload.stoppage || typeof payload.stoppage !== 'object') return;
						const idx = this.stoppages.findIndex(s => s.id === payload.stoppage.id);
						if (idx >= 0) {
							this.$set(this.stoppages, idx, {
								...this.stoppages[idx],
								ended_at    : payload.stoppage.ended_at,
								duration_ms : payload.stoppage.duration_ms,
								rate_after  : payload.stoppage.rate_after,
								status      : 'RESOLVED',
							});
						}
					}
				} catch (e) {
					console.warn('[AlarmStatsWidget] alarm_stoppage payload error:', e);
				}
			});
		},

		_disconnectSocket() {
			if (this.socket) {
				this.socket.off('alarm_stats');
				this.socket.off('alarm_throughput');
				this.socket.off('alarm_stoppage');
				this.socket.disconnect();
				this.socket = null;
			}
			this.wsConnected = false;
		},

		// ─────────────────────────────────────────────────────────────────
		// PUT note update
		// ─────────────────────────────────────────────────────────────────

		/**
		 * Handle note update emitted by StatsStoppageLog.
		 * Payload: { stoppageId, note }
		 */
		handleStoppageNoteUpdate({ stoppageId, note }) {
			const url = this.$URL + '/api/v1/alarm/' + this.alarmId + '/stats/stoppages/' + stoppageId;
			this.$axios.put(url, { note }).catch(err => {
				console.error('[AlarmStatsWidget] PUT note error:', err);
				// Surface note save errors as snackbar
				this._showSnackbar(this.$ml.get('alarm_stats_note_save_error'), 'error');
			});
		},

		// ─────────────────────────────────────────────────────────────────
		// Shift config: localStorage persistence + API save
		// ─────────────────────────────────────────────────────────────────

		_loadShiftConfigFromStorage() {
			try {
				const key  = 'alarm-shift-config-' + this.alarmId;
				const raw  = localStorage.getItem(key);
				if (raw) {
					const parsed = JSON.parse(raw);
					if (parsed && typeof parsed === 'object') {
						this.shiftConfig = {
							shift_start          : parsed.shift_start           || '08:00',
							shift_duration_min   : parsed.shift_duration_min    != null ? parsed.shift_duration_min    : 540,
							target_rate          : parsed.target_rate           != null ? parsed.target_rate           : 0,
							stoppage_threshold_ms: parsed.stoppage_threshold_ms != null ? parsed.stoppage_threshold_ms : 5000,
						};
					}
				}
			} catch (e) {
				console.warn('[AlarmStatsWidget] Could not load shift config from localStorage:', e);
			}
		},

		async handleShiftConfigSave(config) {
			// 1. Persist to backend
			const url = this.$URL + '/api/v1/alarm/' + this.alarmId + '/shift-config';
			try {
				await this.$axios.put(url, config);
			} catch (err) {
				console.error('[AlarmStatsWidget] PUT shift-config error:', err);
				this._showSnackbar(this.$ml.get('alarm_stats_config_save_error'), 'error');
				// Still save locally — don't block the user
			}

			// 2. Persist to localStorage regardless of API result
			try {
				const key = 'alarm-shift-config-' + this.alarmId;
				localStorage.setItem(key, JSON.stringify(config));
			} catch (e) {
				console.warn('[AlarmStatsWidget] Could not save shift config to localStorage:', e);
			}

			// 3. Update local state
			this.shiftConfig = { ...config };

			// 4. Show success feedback
			this._showSnackbar(this.$ml.get('alarm_stats_config_saved'), 'success');
		},

		// ─────────────────────────────────────────────────────────────────
		// Snackbar helper
		// ─────────────────────────────────────────────────────────────────

		_showSnackbar(message, color = 'info', timeout = 4000) {
			this.snackbar.message = message;
			this.snackbar.color   = color;
			this.snackbar.timeout = timeout;
			this.snackbar.show    = true;
		},

		// ─────────────────────────────────────────────────────────────────
		// Utility helpers
		// ─────────────────────────────────────────────────────────────────

		formatDate(ts) {
			if (!ts) return '—';
			try {
				const m = this.$momentz.tz(ts, this.$timezone);
				return m.isValid() ? m.format('M/D/YYYY, h:mm A') : String(ts);
			} catch (e) {
				return ts;
			}
		},

		formatDuration(ms) {
			if (!ms || ms <= 0) return '—';
			const totalSeconds = Math.floor(ms / 1000);
			const hours        = Math.floor(totalSeconds / 3600);
			const minutes      = Math.floor((totalSeconds % 3600) / 60);
			const seconds      = totalSeconds % 60;
			if (hours > 0) return `${hours}h ${minutes}m`;
			if (minutes > 0) return `${minutes}m ${seconds}s`;
			return `${seconds}s`;
		},

	}, // methods
};
</script>
